import { FormEvent, MouseEvent } from 'react';

import { DefaultSearchType } from 'algolia-api/types/DefaultSearchType';
import { DisplayedType } from 'algolia-api/types/DisplayedTypes';
import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { Button } from 'components/Button/Button';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import { LocationInput } from 'components/LocationInput/LocationInput';
import { PopularSearches } from 'components/Search/PopularSearches';
import { SearchButton } from 'components/Search/SearchButton';
import { H4 } from 'components/Text/H4';
import { UncontrolledSearchQueryInputContainer } from 'containers/UncontrolledSearchQueryInputContainer';
import { colors } from 'theme/theme';

import {
  PageHeaderMobileSearchMenuBody,
  PageHeaderMobileSearchMenuHeader,
  PageHeaderMobileSearchMenuHolder,
  PageHeaderSearchInputsMobileForm,
} from './PageHeaderMobileSearchMenu.styled';

type Props = {
  onClose: (event: MouseEvent<HTMLElement>) => void;
  onSearch: (event: FormEvent<HTMLFormElement>) => void;
  selectedType: DisplayedType;
  listingTypes: DisplayedType[];
  clearSearchInput: () => void;
  initialQuery: string;
  query: string;
  onLocationChange: (arg0: SearchLocation | null | undefined) => void;
  setListingType: (listingType: ListingType | DefaultSearchType) => void;
  search: (query: string) => void;
  searchLocation: SearchLocation | undefined;
  setUserQuery: (q: string) => void;
  showPopularSearches?: boolean;
};

export function PageHeaderMobileSearchMenu({
  onClose,
  onSearch,
  selectedType,
  listingTypes,
  clearSearchInput,
  initialQuery,
  query,
  onLocationChange,
  setListingType,
  search,
  searchLocation,
  setUserQuery,
  showPopularSearches = false,
}: Props) {
  return (
    <PageHeaderMobileSearchMenuHolder data-qa-id="mobile-search-menu">
      <PageHeaderMobileSearchMenuHeader>
        <H4 mb={0} color={colors.brandBlue}>
          {getText('Search')}
        </H4>

        <Button
          data-qa-id="close-mobile-search"
          onClick={onClose}
          aria-label={getText('Close search')}
          variant="tertiary"
          noPadding
          icon={{ type: 'normalized', name: 'close' }}
        />
      </PageHeaderMobileSearchMenuHeader>

      <PageHeaderMobileSearchMenuBody>
        <PageHeaderSearchInputsMobileForm
          onSubmit={(event) => {
            event.preventDefault();
            search(query);
            onSearch?.(event);
          }}
          data-qa-id="search-inputs"
        >
          <ListingTypeSelect
            value={selectedType}
            onChange={setListingType}
            listingTypes={listingTypes}
            variant="grey"
          />

          <UncontrolledSearchQueryInputContainer
            initialQuery={initialQuery}
            placeholder={getText('Search by keyword, skill, or interest')}
            xsPlaceholder={getText('Keyword, skill, or interest')}
            onChange={setUserQuery}
            onClearQuery={clearSearchInput}
            presentation="contrast"
            id="mobile-search-query"
            qaId="mobile-search-input"
          />

          <LocationInput
            affixVariant="transparent"
            styleVariant="contrast"
            hasRemoteOptions
            showLocationIcon
            showClearButton
            placeholder={getText('Everywhere')}
            locationText={searchLocation?.text}
            onChange={onLocationChange}
          />

          <SearchButton />
        </PageHeaderSearchInputsMobileForm>
      </PageHeaderMobileSearchMenuBody>

      {showPopularSearches && (
        <PopularSearches
          selectedType={selectedType as ListingType}
          search={search}
          onSearch={onClose}
          setQuery={setUserQuery}
        />
      )}
    </PageHeaderMobileSearchMenuHolder>
  );
}
